import { formatNumberToCurrencyString } from 'utils/number';

import {
  ENTITY_REFERENCE_TYPE,
  ENTITY_REFERENCE_TYPE_LABEL_SINGULAR,
  sanitizeEntityLabel,
} from '../TeamChat.entityReferences.utils';

const formatMoney = value => (value ? formatNumberToCurrencyString(value) : '-');

const formatYesNo = value => (value ? 'Yes' : 'No');

const ENTITY_HOVER_FIELD_BUILDERS = {
  [ENTITY_REFERENCE_TYPE.BORROWER]: details => [
    { label: 'Name', value: details?.fullName || '-' },
    { label: 'Type', value: details?.borrowerType || '-' },
  ],
  [ENTITY_REFERENCE_TYPE.INCOME]: details => [
    { label: 'Type', value: details?.incomeTypeName || '-' },
    { label: 'Monthly Amount', value: formatMoney(details?.monthlyIncome) },
    { label: 'Employer', value: details?.employerName || '-' },
  ],
  [ENTITY_REFERENCE_TYPE.ASSET]: details => [
    { label: 'Institution', value: details?.institution || '-' },
    { label: 'Balance', value: formatMoney(details?.balance) },
    { label: 'Verified', value: formatYesNo(details?.isVerified) },
  ],
  [ENTITY_REFERENCE_TYPE.LIABILITY]: details => [
    { label: 'Balance', value: formatMoney(details?.unpaidBalance) },
    { label: 'Payment/Mo', value: formatMoney(details?.monthlyPayment) },
    { label: 'Payoff Required', value: formatYesNo(details?.payoffRequired) },
  ],
  [ENTITY_REFERENCE_TYPE.REO]: details => [
    { label: 'Address', value: details?.address || '-' },
    { label: 'Status', value: details?.status || '-' },
    { label: 'Occupancy', value: details?.occupancy || '-' },
    { label: 'Net Income', value: formatMoney(details?.netIncome) },
    { label: 'Subject Property', value: formatYesNo(details?.isSubjectProperty) },
  ],
};

export const getEntityHoverFields = (entityType, details) =>
  ENTITY_HOVER_FIELD_BUILDERS[entityType]?.(details) ?? [];

export const EntityReferenceChipState = {
  NORMAL: 'normal',
  DELTA: 'delta',
  TOMBSTONE: 'tombstone',
  INERT: 'inert',
};

/**
 * Resolves the chip's visual/interaction state (frozen design, DEV-5446):
 * - INERT: no loan chat context resolvable at all — label only, no hover, no nav.
 * - TOMBSTONE: this loan's data IS loaded and the id is no longer present — the item was removed.
 * - DELTA: the item still exists but its current label differs from the token's snapshot label.
 * - NORMAL: matches, or the loan's live data isn't loaded here (unknown-live-state — the chip
 *   still fully supports navigation, since navigation only needs the ids, not the live record).
 */
const ENTITY_CHIP_ARIA_STATE_SUFFIX = {
  [EntityReferenceChipState.TOMBSTONE]: ' (removed)',
  [EntityReferenceChipState.DELTA]: ' (updated)',
};

export const getEntityChipAriaLabel = ({ entityType, label, state }) =>
  `${ENTITY_REFERENCE_TYPE_LABEL_SINGULAR[entityType]} reference: ${label}${
    ENTITY_CHIP_ARIA_STATE_SUFFIX[state] ?? ''
  }`;

export const getEntityChipState = ({
  applicationId,
  isLoanDataLoaded,
  currentCandidate,
  label,
}) => {
  if (!applicationId) {
    return EntityReferenceChipState.INERT;
  }

  if (isLoanDataLoaded && !currentCandidate) {
    return EntityReferenceChipState.TOMBSTONE;
  }

  if (
    isLoanDataLoaded &&
    currentCandidate &&
    sanitizeEntityLabel(currentCandidate.label) !== label
  ) {
    return EntityReferenceChipState.DELTA;
  }

  return EntityReferenceChipState.NORMAL;
};
